import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  Building2, Users, Plus, LogOut, MapPin, Sprout, Trash2, Power, KeyRound, Shield, ExternalLink, RotateCcw, Home,
} from 'lucide-react';

import { useNavigate } from 'react-router-dom';
import { startImpersonating } from '@/lib/axiosClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

const SuperAdminDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [farms, setFarms] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('farms');

  // create farm dialog state
  const emptyNew = {
    name: '', location: '', contact_name: '', contact_phone: '',
    contact_email: '', mushroom_type: 'Button',
    owner_username: '', owner_password: '', owner_full_name: '',
  };
  const [createOpen, setCreateOpen] = useState(false);
  const [newFarm, setNewFarm] = useState(emptyNew);

  // reset pwd dialog
  const [pwdUser, setPwdUser] = useState(null);
  const [newPwd, setNewPwd] = useState('');

  // assign-farm dialog
  const [assignUser, setAssignUser] = useState(null);
  const [assignFarmId, setAssignFarmId] = useState('');

  // user list filters
  const [userSearch, setUserSearch] = useState('');
  const [userFarmFilter, setUserFarmFilter] = useState('all');

  // farm-manager create dialog
  const emptyFM = { username: '', password: '', full_name: '', farm_ids: [] };
  const [fmOpen, setFmOpen] = useState(false);
  const [newFM, setNewFM] = useState(emptyFM);
  // farm-manager edit (re-assign farms)
  const [fmEdit, setFmEdit] = useState(null);  // {id, username, farm_ids}

  const fetchFarms = async () => {
    try {
      const { data } = await axios.get(`${API}/super/farms`, { headers: authHeaders() });
      setFarms(data.farms || []);
    } catch (e) {
      toast.error('Failed to load farms');
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/super/users`, { headers: authHeaders() });
      setUsers(data.users || []);
    } catch (e) {
      toast.error('Failed to load users');
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchFarms(), fetchUsers()]);
      setLoading(false);
    })();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newFarm.name.trim()) return toast.error('Farm name required');
    if (newFarm.owner_username && (!newFarm.owner_password || newFarm.owner_password.length < 6)) {
      return toast.error('Owner password must be at least 6 characters');
    }
    try {
      const { data } = await axios.post(`${API}/super/farms`, newFarm, { headers: authHeaders() });
      toast.success(data.message || 'Farm created');
      setCreateOpen(false);
      setNewFarm(emptyNew);
      await Promise.all([fetchFarms(), fetchUsers()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create farm');
    }
  };

  const handleToggleStatus = async (farm) => {
    const next = farm.status === 'active' ? 'disabled' : 'active';
    if (!window.confirm(`${next === 'disabled' ? 'Disable' : 'Re-enable'} farm "${farm.name}"?`)) return;
    try {
      await axios.put(`${API}/super/farms/${farm.id}`, { status: next }, { headers: authHeaders() });
      toast.success(`Farm ${next === 'disabled' ? 'disabled' : 'enabled'}`);
      fetchFarms();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDelete = async (farm) => {
    if (farm.id === 'main-farm') return toast.error('Main Farm cannot be deleted');
    if (!window.confirm(`Permanently DELETE farm "${farm.name}" and ALL its data (rooms, users, batches, cycles)?\n\nThis cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/super/farms/${farm.id}?cascade=true`, { headers: authHeaders() });
      toast.success(`Farm "${farm.name}" deleted`);
      await Promise.all([fetchFarms(), fetchUsers()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleOpenFarm = (farm) => {
    startImpersonating(farm.id, farm.name);
    navigate('/owner');
    setTimeout(() => window.location.reload(), 50);
  };

  const handleRerunSetup = async (farm) => {
    const ok = window.confirm(
      `⚠️ Re-run the onboarding wizard for "${farm.name}"?\n\n` +
      `This re-opens the setup wizard so the current state can be re-entered. ` +
      `It does NOT delete existing rooms, cycles or batches — but seeding the same running room twice will be refused.\n\n` +
      `Only proceed if you intend to re-do onboarding for this farm. Continue?`
    );
    if (!ok) return;
    try {
      // impersonate so the X-Farm-Id header is attached, then reset the flag
      startImpersonating(farm.id, farm.name);
      await axios.post(`${API}/setup/reset`, {}, { headers: { ...authHeaders(), 'X-Farm-Id': farm.id } });
      toast.success(`Setup wizard re-opened for "${farm.name}". Opening farm...`);
      navigate('/owner');
      setTimeout(() => window.location.reload(), 50);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to re-run setup');
    }
  };

  const handleCreateFM = async (e) => {
    e.preventDefault();
    if (!newFM.username.trim()) return toast.error('Username required');
    if (!newFM.password || newFM.password.length < 6) return toast.error('Password must be at least 6 chars');
    if (newFM.farm_ids.length === 0) return toast.error('Pick at least one farm');
    try {
      await axios.post(`${API}/super/farm-managers`, newFM, { headers: authHeaders() });
      toast.success(`Farm manager '${newFM.username}' created`);
      setFmOpen(false);
      setNewFM(emptyFM);
      await fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
  };

  const handleUpdateFM = async () => {
    if (!fmEdit) return;
    if ((fmEdit.farm_ids || []).length === 0) return toast.error('Pick at least one farm');
    try {
      await axios.put(
        `${API}/super/farm-managers/${fmEdit.id}/farms`,
        { farm_ids: fmEdit.farm_ids },
        { headers: authHeaders() },
      );
      toast.success('Farm assignments updated');
      setFmEdit(null);
      await fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleAssignFarm = async () => {
    if (!assignUser) return;
    try {
      const { data } = await axios.put(
        `${API}/super/users/${assignUser.id}/assign-farm`,
        { farm_id: assignFarmId || null },
        { headers: authHeaders() },
      );
      toast.success(data.message || 'Farm assigned');
      setAssignUser(null);
      setAssignFarmId('');
      await fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign farm');
    }
  };

  const handleResetPassword = async () => {    if (!newPwd || newPwd.length < 6) return toast.error('Password must be at least 6 chars');
    try {
      await axios.post(
        `${API}/super/users/${pwdUser.id}/reset-password`,
        { password: newPwd },
        { headers: authHeaders() },
      );
      toast.success(`Password reset for ${pwdUser.username}`);
      setPwdUser(null);
      setNewPwd('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDeleteUser = async (u) => {    if (!window.confirm(`Permanently delete user "${u.username}" (${u.farm_name || 'unassigned'})?`)) return;
    try {
      await axios.delete(`${API}/super/users/${u.id}`, { headers: authHeaders() });
      toast.success(`User "${u.username}" deleted`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  // derived: filter user list by search + farm
  const filteredUsers = users.filter((u) => {
    if (userFarmFilter === '__unassigned') {
      if (u.farm_id || u.role === 'super_admin') return false;
    } else if (userFarmFilter === '__platform') {
      if (u.role !== 'super_admin') return false;
    } else if (userFarmFilter !== 'all') {
      if (u.farm_id !== userFarmFilter) return false;
    }
    if (!userSearch) return true;
    const q = userSearch.toLowerCase();
    return (
      (u.username || '').toLowerCase().includes(q) ||
      (u.full_name || '').toLowerCase().includes(q)
    );
  });

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F9FAF9]"><div>Loading...</div></div>;
  }

  return (
    <div className="min-h-screen bg-[#F9FAF9]">
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-7 h-7 text-[#1A4D2E]" />
          <div>
            <h1 className="text-xl font-bold text-[#1A4D2E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              A3ROS Platform Admin
            </h1>
            <p className="text-xs text-gray-500">Logged in as <strong>{user?.username}</strong> · Super Admin</p>
          </div>
        </div>
        <Button variant="outline" onClick={onLogout} data-testid="logout-btn">
          <LogOut className="w-4 h-4 mr-2" /> Logout
        </Button>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-5 flex items-center gap-4">
              <Building2 className="w-10 h-10 text-[#1A4D2E]" />
              <div>
                <p className="text-sm text-gray-500">Total Farms</p>
                <p className="text-3xl font-bold">{farms.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5 flex items-center gap-4">
              <Users className="w-10 h-10 text-blue-600" />
              <div>
                <p className="text-sm text-gray-500">Total Users</p>
                <p className="text-3xl font-bold">{users.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5 flex items-center gap-4">
              <Sprout className="w-10 h-10 text-amber-600" />
              <div>
                <p className="text-sm text-gray-500">Active Farms</p>
                <p className="text-3xl font-bold">{farms.filter((f) => f.status === 'active').length}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            data-testid="tab-farms"
            onClick={() => setTab('farms')}
            className={`px-4 py-2 rounded-lg font-medium ${tab === 'farms' ? 'bg-[#1A4D2E] text-white' : 'bg-white text-gray-700 border border-gray-200'}`}
          >
            Farms ({farms.length})
          </button>
          <button
            data-testid="tab-users"
            onClick={() => setTab('users')}
            className={`px-4 py-2 rounded-lg font-medium ${tab === 'users' ? 'bg-[#1A4D2E] text-white' : 'bg-white text-gray-700 border border-gray-200'}`}
          >
            All Users ({users.length})
          </button>
          <button
            data-testid="tab-managers"
            onClick={() => setTab('managers')}
            className={`px-4 py-2 rounded-lg font-medium ${tab === 'managers' ? 'bg-[#1A4D2E] text-white' : 'bg-white text-gray-700 border border-gray-200'}`}
          >
            Farm Managers ({users.filter((u) => u.role === 'farm_manager').length})
          </button>
        </div>

        {tab === 'farms' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Farms</CardTitle>
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#1A4D2E] hover:bg-[#143D23] text-white" data-testid="new-farm-btn">
                    <Plus className="w-4 h-4 mr-2" /> New Farm
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create New Farm</DialogTitle>
                    <DialogDescription>
                      Set up a new farm and (optionally) its first Owner login. The farm gets 16 default rooms.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label>Farm Name *</Label>
                        <Input data-testid="farm-name" value={newFarm.name} onChange={(e) => setNewFarm({ ...newFarm, name: e.target.value })} required />
                      </div>
                      <div>
                        <Label>Location</Label>
                        <Input data-testid="farm-location" value={newFarm.location} onChange={(e) => setNewFarm({ ...newFarm, location: e.target.value })} placeholder="City, State" />
                      </div>
                      <div>
                        <Label>Mushroom Type</Label>
                        <select
                          value={newFarm.mushroom_type}
                          onChange={(e) => setNewFarm({ ...newFarm, mushroom_type: e.target.value })}
                          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg bg-white"
                        >
                          <option>Button</option>
                          <option>Oyster</option>
                          <option>Shiitake</option>
                          <option>Other</option>
                        </select>
                      </div>
                      <div>
                        <Label>Contact Name</Label>
                        <Input value={newFarm.contact_name} onChange={(e) => setNewFarm({ ...newFarm, contact_name: e.target.value })} />
                      </div>
                      <div>
                        <Label>Contact Phone</Label>
                        <Input value={newFarm.contact_phone} onChange={(e) => setNewFarm({ ...newFarm, contact_phone: e.target.value })} />
                      </div>
                      <div>
                        <Label>Contact Email</Label>
                        <Input type="email" value={newFarm.contact_email} onChange={(e) => setNewFarm({ ...newFarm, contact_email: e.target.value })} />
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <p className="text-sm font-medium text-gray-700 mb-3">Initial Owner Login (optional but recommended)</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <Label>Owner Username</Label>
                          <Input data-testid="owner-username" value={newFarm.owner_username} onChange={(e) => setNewFarm({ ...newFarm, owner_username: e.target.value })} />
                        </div>
                        <div>
                          <Label>Owner Full Name</Label>
                          <Input value={newFarm.owner_full_name} onChange={(e) => setNewFarm({ ...newFarm, owner_full_name: e.target.value })} />
                        </div>
                        <div>
                          <Label>Owner Password</Label>
                          <Input data-testid="owner-password" type="password" value={newFarm.owner_password} onChange={(e) => setNewFarm({ ...newFarm, owner_password: e.target.value })} placeholder="Min 6 chars" />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Owner can later add more users (grower, supervisors, technicians) inside their farm.
                      </p>
                    </div>

                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-[#1A4D2E] hover:bg-[#143D23] text-white" data-testid="create-farm-submit">
                        Create Farm
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-gray-200 text-gray-500">
                      <th className="py-2 pr-4">Name</th>
                      <th className="py-2 pr-4">Location</th>
                      <th className="py-2 pr-4">Mushroom</th>
                      <th className="py-2 pr-4">Rooms</th>
                      <th className="py-2 pr-4">Users</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {farms.map((f) => (
                      <tr key={f.id} className="border-b border-gray-100">
                        <td className="py-3 pr-4 font-medium">{f.name}</td>
                        <td className="py-3 pr-4 text-gray-600">
                          {f.location ? (<span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{f.location}</span>) : '—'}
                        </td>
                        <td className="py-3 pr-4">{f.mushroom_type || '—'}</td>
                        <td className="py-3 pr-4">{f.rooms?.length || 0}</td>
                        <td className="py-3 pr-4">{f.user_count ?? '—'}</td>
                        <td className="py-3 pr-4">
                          <Badge variant={f.status === 'active' ? 'default' : 'secondary'} className={f.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}>
                            {f.status}
                          </Badge>
                        </td>
                        <td className="py-3 pr-4">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="bg-[#1A4D2E] hover:bg-[#143D23] text-white"
                              onClick={() => handleOpenFarm(f)}
                              data-testid={`open-farm-${f.id}`}
                              title="View this farm as Owner"
                            >
                              <ExternalLink className="w-3.5 h-3.5 mr-1" />Open
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleToggleStatus(f)} title={f.status === 'active' ? 'Disable' : 'Enable'}>
                              <Power className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleRerunSetup(f)} title="Re-run onboarding wizard" data-testid={`rerun-setup-${f.id}`}>
                              <RotateCcw className="w-3.5 h-3.5" />
                            </Button>
                            {f.id !== 'main-farm' && (
                              <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(f)} title="Delete">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {tab === 'users' && (
          <Card>
            <CardHeader>
              <CardTitle>All Users</CardTitle>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <div className="flex-1 min-w-[200px]">
                  <Input
                    placeholder="Search by username or name…"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    data-testid="user-search"
                  />
                </div>
                <select
                  value={userFarmFilter}
                  onChange={(e) => setUserFarmFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
                  data-testid="user-farm-filter"
                >
                  <option value="all">All farms ({users.length})</option>
                  {farms.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name} ({users.filter((u) => u.farm_id === f.id).length})
                    </option>
                  ))}
                  <option value="__unassigned">— Unassigned ({users.filter((u) => !u.farm_id && u.role !== 'super_admin').length})</option>
                  <option value="__platform">— Platform / Super Admin ({users.filter((u) => u.role === 'super_admin').length})</option>
                </select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-gray-200 text-gray-500">
                      <th className="py-2 pr-4">Username</th>
                      <th className="py-2 pr-4">Full Name</th>
                      <th className="py-2 pr-4">Role</th>
                      <th className="py-2 pr-4">Farm</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 && (
                      <tr><td colSpan={6} className="py-6 text-center text-gray-500">No users match.</td></tr>
                    )}
                    {filteredUsers.map((u) => (
                      <tr key={u.id} className="border-b border-gray-100">
                        <td className="py-2 pr-4 font-medium">{u.username}</td>
                        <td className="py-2 pr-4">{u.full_name}</td>
                        <td className="py-2 pr-4">
                          <Badge variant="secondary" className={u.role === 'super_admin' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700'}>
                            {u.role}
                          </Badge>
                        </td>
                        <td className="py-2 pr-4 text-gray-600">{u.farm_name || (u.role === 'super_admin' ? '— (platform)' : '— Unassigned')}</td>
                        <td className="py-2 pr-4">
                          <Badge variant="secondary" className={u.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                            {u.status}
                          </Badge>
                        </td>
                        <td className="py-2 pr-4">
                          <div className="flex gap-2">
                            {u.role !== 'super_admin' && u.role !== 'farm_manager' && (
                              <Button
                                size="sm"
                                variant={u.farm_id ? 'outline' : 'default'}
                                className={u.farm_id ? '' : 'bg-amber-600 hover:bg-amber-700 text-white'}
                                onClick={() => { setAssignUser(u); setAssignFarmId(u.farm_id || ''); }}
                                title={u.farm_id ? 'Change farm' : 'Assign to a farm'}
                                data-testid={`assign-farm-${u.id}`}
                              >
                                <Home className="w-3.5 h-3.5 mr-1" />
                                {u.farm_id ? 'Move' : 'Assign Farm'}
                              </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => { setPwdUser(u); setNewPwd(''); }} title="Reset password">
                              <KeyRound className="w-3.5 h-3.5" />
                            </Button>
                            {u.id !== user?.id && (
                              <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteUser(u)} title="Delete user">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {tab === 'managers' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Farm Managers</CardTitle>
              <Button
                className="bg-[#1A4D2E] hover:bg-[#143D23] text-white"
                onClick={() => { setNewFM(emptyFM); setFmOpen(true); }}
                data-testid="new-fm-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> New Farm Manager
              </Button>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-3">
                A Farm Manager has Grower-level access but can operate multiple farms.
                Useful when one person manages farms for several owners.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-gray-200 text-gray-500">
                      <th className="py-2 pr-4">Username</th>
                      <th className="py-2 pr-4">Full Name</th>
                      <th className="py-2 pr-4">Farms</th>
                      <th className="py-2 pr-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.filter((u) => u.role === 'farm_manager').length === 0 && (
                      <tr><td colSpan={4} className="py-6 text-center text-gray-500">No farm managers yet. Click &quot;New Farm Manager&quot;.</td></tr>
                    )}
                    {users.filter((u) => u.role === 'farm_manager').map((u) => (
                      <tr key={u.id} className="border-b border-gray-100">
                        <td className="py-2 pr-4 font-medium">{u.username}</td>
                        <td className="py-2 pr-4">{u.full_name}</td>
                        <td className="py-2 pr-4">
                          <div className="flex flex-wrap gap-1">
                            {(u.farms || []).map((f) => (
                              <Badge key={f.id} className="bg-blue-100 text-blue-800">{f.name}</Badge>
                            ))}
                            {(!u.farms || u.farms.length === 0) && <span className="text-gray-400">— none</span>}
                          </div>
                        </td>
                        <td className="py-2 pr-4">
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => setFmEdit({ id: u.id, username: u.username, farm_ids: [...(u.farm_ids || [])] })} title="Edit farm assignments">
                              <MapPin className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => { setPwdUser(u); setNewPwd(''); }} title="Reset password">
                              <KeyRound className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteUser(u)} title="Delete user">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Farm Manager Dialog */}
      <Dialog open={fmOpen} onOpenChange={setFmOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Create Farm Manager</DialogTitle>
            <DialogDescription>
              Operator with Grower-level access across multiple farms.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateFM} className="space-y-3">
            <div>
              <Label>Full Name *</Label>
              <Input value={newFM.full_name} onChange={(e) => setNewFM({ ...newFM, full_name: e.target.value })} required data-testid="fm-fullname" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Username *</Label>
                <Input value={newFM.username} onChange={(e) => setNewFM({ ...newFM, username: e.target.value.trim() })} required data-testid="fm-username" />
              </div>
              <div>
                <Label>Password *</Label>
                <Input type="password" value={newFM.password} onChange={(e) => setNewFM({ ...newFM, password: e.target.value })} placeholder="Min 6 chars" required data-testid="fm-password" />
              </div>
            </div>
            <div>
              <Label>Assign to farms * (check all that apply)</Label>
              <div className="mt-2 border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto space-y-1">
                {farms.map((f) => (
                  <label key={f.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newFM.farm_ids.includes(f.id)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...newFM.farm_ids, f.id]
                          : newFM.farm_ids.filter((x) => x !== f.id);
                        setNewFM({ ...newFM, farm_ids: next });
                      }}
                      data-testid={`fm-farm-${f.id}`}
                    />
                    <span>{f.name}</span>
                    <span className="text-xs text-gray-500">({f.location || '—'})</span>
                  </label>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFmOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1A4D2E] hover:bg-[#143D23] text-white" data-testid="fm-submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit farm assignments Dialog */}
      <Dialog open={!!fmEdit} onOpenChange={(o) => { if (!o) setFmEdit(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit farm assignments — {fmEdit?.username}</DialogTitle>
          </DialogHeader>
          {fmEdit && (
            <div className="border border-gray-200 rounded-lg p-3 max-h-64 overflow-y-auto space-y-1">
              {farms.map((f) => (
                <label key={f.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fmEdit.farm_ids.includes(f.id)}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...fmEdit.farm_ids, f.id]
                        : fmEdit.farm_ids.filter((x) => x !== f.id);
                      setFmEdit({ ...fmEdit, farm_ids: next });
                    }}
                  />
                  <span>{f.name}</span>
                </label>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFmEdit(null)}>Cancel</Button>
            <Button onClick={handleUpdateFM} className="bg-[#1A4D2E] hover:bg-[#143D23] text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Farm modal */}
      <Dialog open={!!assignUser} onOpenChange={(o) => { if (!o) { setAssignUser(null); setAssignFarmId(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Farm — {assignUser?.username}</DialogTitle>
            <DialogDescription>
              Link <strong>{assignUser?.username}</strong> ({assignUser?.role}) to a farm.
              Current: {assignUser?.farm_name || 'Unassigned'}.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Farm</Label>
            <select
              value={assignFarmId}
              onChange={(e) => setAssignFarmId(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg bg-white"
              data-testid="assign-farm-select"
            >
              <option value="">— Unassigned —</option>
              {farms.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAssignUser(null); setAssignFarmId(''); }}>Cancel</Button>
            <Button onClick={handleAssignFarm} className="bg-[#1A4D2E] hover:bg-[#143D23] text-white" data-testid="assign-farm-confirm">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password modal */}
      <Dialog open={!!pwdUser} onOpenChange={(o) => { if (!o) { setPwdUser(null); setNewPwd(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{pwdUser?.username}</strong> ({pwdUser?.farm_name || '—'}).
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>New Password</Label>
            <Input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="Min 6 chars" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPwdUser(null); setNewPwd(''); }}>Cancel</Button>
            <Button onClick={handleResetPassword} className="bg-[#1A4D2E] hover:bg-[#143D23] text-white">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminDashboard;
